// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import GroupedLinesHeader from "@eisinc/App/Components/Cart/GroupedLinesHeading";
import ProductFeedAndSalesPixel from "@eisinc/App/Components/ProductFeedAndSalesPixel/ProductFeedAndSalesPixel";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import Tab from "@eisinc/MobiusOverrides/Tab";
import TabGroup from "@eisinc/MobiusOverrides/TabGroup";
import CartLineCardExpanded from "@eisinc/Overrides/Widgets/Cart/CartLineCardExpanded";
import { CartComponent } from "@eisinc/Overrides/Widgets/Cart/CartLines";
import { allProductsLabel } from "@eisinc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitProductList";
import { SHIPS_ASAP } from "@eisinc/XDL/constants";
import { formatDate, parseBoolean } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CartLinesListStyles } from "@insite/content-library/Components/CartLinesList";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state) as any;
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    return {
        cartState,
        promotionsDataView: getPromotionsDataView(state, state.pages.orderConfirmation.cartId),
        settingsCollection,
        // XNG-Change
        shippingCostBreakdown: (state as CustomAppState).xCheckout.shippingCostBreakdown,
        displayTabbedProductList: parseBoolean(settingsCollection?.customSettings?.displayTabbedProductList),
        hasScheduledDeliveries: parseBoolean(cartState.value?.properties?.hasScheduledDeliveries),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OrderConfirmationProductListStyles {
    centeringWrapper?: InjectableCss;
    orderLinesList?: CartLinesListStyles;
    // XNG-Changes
    container?: InjectableCss;
    heading?: TypographyPresentationProps;
    smallHeadingAndText?: SmallHeadingAndTextStyles;
    shippingCostWrapper?: InjectableCss;
}

export const productListStyles: OrderConfirmationProductListStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 300px;
        `,
    },
};

const styles = productListStyles;

const OrderConfirmationProductList: FC<Props> = ({
    cartState,
    promotionsDataView,
    settingsCollection,
    // XNG-Change
    shippingCostBreakdown,
    displayTabbedProductList,
    hasScheduledDeliveries,
}) => {
    const [groupedCartlinesByDate, setGroupedCartlinesByDate] = useState<Map<string, CartLineModel[]>>(() => new Map());
    const [currentTab, setCurrentTab] = useState("");

    React.useEffect(() => {
        if (hasScheduledDeliveries) {
            groupByRequestedDate();
        } else {
            setGroupedCartlinesByDate(() => new Map());
        }
    }, [hasScheduledDeliveries]);

    const groupByRequestedDate = () => {
        const cartLines = (cartState?.value?.cartLines as CartLineModel[]) || [];
        const groupedCartlines = new Map<string, CartLineModel[]>();

        groupedCartlines.set(allProductsLabel, [...cartLines]);
        groupedCartlines.set(SHIPS_ASAP, []);

        for (const item of cartLines) {
            let date = item.properties?.requestedByDate || SHIPS_ASAP;
            if (date !== SHIPS_ASAP) {
                date = formatDate(convertDateOnlyStringToDate(date) ?? null);
            }

            if (!groupedCartlines.has(date)) {
                groupedCartlines.set(date, []);
            }

            groupedCartlines.get(date)?.push(item);
        }

        const sortedDateEntries = [...groupedCartlines.entries()]
            .filter(([key]) => key !== allProductsLabel && key !== SHIPS_ASAP)
            .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());

        const sortedGroupedCartLines = new Map<string, CartLineModel[]>();
        sortedGroupedCartLines.set(allProductsLabel, groupedCartlines.get(allProductsLabel)!);

        const asapLines = groupedCartlines.get(SHIPS_ASAP);
        if (asapLines && asapLines.length > 0) {
            sortedGroupedCartLines.set(SHIPS_ASAP, asapLines);
        } else {
            sortedGroupedCartLines.delete(SHIPS_ASAP);
        }

        for (const [key, value] of sortedDateEntries) {
            sortedGroupedCartLines.set(key, value);
        }

        setGroupedCartlinesByDate(sortedGroupedCartLines);
        setCurrentTab(allProductsLabel);
    };

    if (!cartState.value || !promotionsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    // XNG-Change
    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        if (tabKey) {
            setCurrentTab(tabKey);
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="OrderConfirmationProductList">
            {cartState.value.fulfillmentMethod === FulfillmentMethod.PickUp ? (
                <Typography {...styles.heading}>{translate("Order Lines")}</Typography>
            ) : (
                <Typography {...styles.heading}>{translate("Product Shipments")}</Typography>
            )}
            <ProductFeedAndSalesPixel />
            {/* XNG-Change: display shipping cost */}
            {cartState?.value.fulfillmentMethod === FulfillmentMethod.Ship &&
                (!groupedCartlinesByDate.size || !displayTabbedProductList) && (
                    <SmallHeadingAndText
                        heading={translate("Total Shipping Cost")}
                        text={cartState?.value.properties?.shipping}
                        extendedStyles={styles.smallHeadingAndText}
                    />
                )}
            {/* XNG-Change: display tabs */}
            {groupedCartlinesByDate.size > 0 && displayTabbedProductList ? (
                <>
                    <TabGroup current={currentTab} onTabChange={onTabChange} displayAsDropDownOnMobile={true}>
                        {[...groupedCartlinesByDate.entries()].map(([date, lines]) => {
                            return (
                                <Tab key={date} headline={date} tabKey={date}>
                                    <StyledWrapper {...styles.shippingCostWrapper}>
                                        {groupedCartlinesByDate.size > 0 && currentTab !== allProductsLabel && (
                                            <SmallHeadingAndText
                                                heading={translate("Items")}
                                                text={groupedCartlinesByDate.get(currentTab)?.length ?? 0}
                                                extendedStyles={styles.smallHeadingAndText}
                                            />
                                        )}

                                        <SmallHeadingAndText
                                            heading={
                                                hasScheduledDeliveries && currentTab !== allProductsLabel
                                                    ? translate("Shipping Cost")
                                                    : translate("Total Shipping Cost")
                                            }
                                            text={
                                                hasScheduledDeliveries
                                                    ? shippingCostBreakdown?.[currentTab]?.AmountDisplay ||
                                                      cartState?.value.properties?.shipping
                                                    : cartState?.value.properties?.shipping
                                            }
                                            extendedStyles={styles.smallHeadingAndText}
                                        />
                                    </StyledWrapper>

                                    <GroupedLinesHeader
                                        editable={false}
                                        showQtyOrderedShipped={false}
                                        showReqDeliveryDate={
                                            cartState?.value?.fulfillmentMethod === FulfillmentMethod.Ship
                                        }
                                    />

                                    {(lines as CartLineModel[]).map((line: CartLineModel) => {
                                        const promotions = promotionsDataView.value;

                                        const matchingPromotions = promotions
                                            ? promotions.filter(promo => promo.orderLineId === line.id)
                                            : [];
                                        return (
                                            <CartLineContext.Provider
                                                value={line}
                                                key={`${line.id}_${line.properties?.requestedByDate}`}
                                            >
                                                <CartLineCardExpanded
                                                    cart={cartState.value!}
                                                    promotions={matchingPromotions}
                                                    productSettings={settingsCollection.productSettings}
                                                    showLineNotes={true}
                                                    showRemoveAction={false}
                                                    productId={line?.productId}
                                                    showInventoryAvailability={false}
                                                    showAddToList={false}
                                                    showBadging={true}
                                                    editable={false}
                                                    editableQty={false}
                                                    showPONumber={false}
                                                    showDate={
                                                        cartState?.value?.fulfillmentMethod === FulfillmentMethod.Ship
                                                    }
                                                    showQtyOrderedShipped={false}
                                                    showAddToCart={false}
                                                    showShipmentData={false}
                                                    productIdAsId={false}
                                                    showQuotedPrice={false}
                                                    useModalForCertification={true}
                                                    dontShowCertification={false}
                                                />
                                            </CartLineContext.Provider>
                                        );
                                    })}
                                </Tab>
                            );
                        })}
                    </TabGroup>
                </>
            ) : (
                <CartComponent
                    customCart={cartState.value}
                    customPromotion={promotionsDataView.value}
                    options={["date", "badging"]}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderConfirmationProductList),
    definition: {
        displayName: "Product List",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
    },
};

export default widgetModule;
